import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
@Component({
  selector: 'app-edit-shape',
  templateUrl: './edit-shape.component.html',
  styleUrls: ['./edit-shape.component.scss']
})
export class EditShapeComponent implements OnInit {
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;
  @ViewChild('svgOverlay', { static: true }) svgOverlay!: ElementRef<SVGSVGElement>;

  // Cấu hình
  width = 650;
  height = 450;
  bgImage = 'https://images.unsplash.com/photo-1518791841217-8f162f1e1131?w=1200&q=80'; // demo
  bgColor = '#ffffff';
  // bgTransparent = 'linear-gradient(45deg, #eee 25%, transparent 25%), linear-gradient(-45deg, #eee 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eee 75%), linear-gradient(-45deg, transparent 75%, #eee 75%)';

  shape: ClipShape;

  // Bước và đơn vị
  units: Units = 'percent';
  gridStepPercent = 5; // snap step in percent

  // points: Point[] = [];
  active: ActiveHandle = null;
  dragging = false;

  // polygonPointsStr = ''; // cached string dùng trong template
  enableOverlay = true;
  opacityOverlay = 0.3;

  /* Xử lý cho khi chỉnh sửa trực tiếp */
  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement | HTMLTextAreaElement>;
  editing = false; // Kiểm tra khi vào chỉnh sửa trực tiếp hay không
  editingValue = ''; // Biến trung gian khi chỉnh sửa trực tiếp

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<EditShapeComponent>,
    private cd: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    // default shape: polygon rectangle
    this.shape = {
      kind: 'polygon',
      units: 'percent',
      points: [
        { x: 5, y: 5 },
        { x: 95, y: 5 },
        { x: 95, y: 95 },
        { x: 5, y: 95 }
      ]
    } as PolygonShape;
  }

  ngOnInit(): void {
    this.bgImage = this.data.optionDisplay == 'image' ? this.data.bgImage : this.bgImage;
    // Hình chữ nhật mặc định có lề (theo phần trăm)
    this.loadShape();

    // Lắng click ra ngoài (backdrop)
    // đoạn này để đóng mà modal và trả về result, nếu ko sẽ ko cập nhật giao diện
    this.dialogRef.backdropClick().subscribe(() => {
      this.close();
    });
  }

  ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.cd.detectChanges();
    });
  }

  /**
   * Phân tích chuỗi clip-path (chỉ hỗ trợ polygon) để trả về mảng các cặp tọa độ [x, y] ở dạng chuỗi.
   * @param clipPathString Chuỗi CSS clip-path, ví dụ: "polygon(50% 0%, 100% 50%)"
   * @returns Mảng các cặp [x, y] (kiểu tuple) ở dạng chuỗi: Array<[string, string]>.
   */
  private parseClipPath(clip: string): any {
    clip = clip.trim();
    const units = '%';
  
    // --- 1. POLYGON ---
    if (clip.startsWith('polygon(')) {
      const content = clip.replace(/^polygon\(|\)$/g, '');
      const points = content.split(',').map(p => {
        const [x, y] = p.trim().split(/\s+/);
        return {
          x: this.normalizeToPercent(x),
          y: this.normalizeToPercent(y)
        };
      });
  
      return {
        kind: 'polygon',
        units,
        points
      };
    }
  
    // --- 2. INSET ---
    if (clip.startsWith('inset(')) {
      const content = clip.replace(/^inset\(|\)$/g, '').trim();
      const parts = content.split(/\s+/);
  
      // inset(top right bottom left)
      const top = this.normalizeToPercent(parts[0]);
      const right = this.normalizeToPercent(parts[1] ?? parts[0]);
      const bottom = this.normalizeToPercent(parts[2] ?? parts[0]);
      const left = this.normalizeToPercent(parts[3] ?? parts[1] ?? parts[0]);
  
      return {
        kind: 'inset',
        units,
        top,
        right,
        bottom,
        left,
        borderRadius: '0'
      };
    }
  
    // --- 3. CIRCLE ---
    if (clip.startsWith('circle(')) {
      const content = clip.replace(/^circle\(|\)$/g, '').trim();
      // circle(35% at 50% 50%)
      const [radiusStr, , cxStr, cyStr] = content.split(/\s+/);
  
      return {
        kind: 'circle',
        units,
        r: this.normalizeToPercent(radiusStr),
        cx: this.normalizeToPercent(cxStr),
        cy: this.normalizeToPercent(cyStr)
      };
    }
  
    // --- 4. ELLIPSE ---
    if (clip.startsWith('ellipse(')) {
      const content = clip.replace(/^ellipse\(|\)$/g, '').trim();
      // ellipse(25% 50% at 50% 50%)
      const [rxStr, ryStr, , cxStr, cyStr] = content.split(/\s+/);
  
      return {
        kind: 'ellipse',
        units,
        rx: this.normalizeToPercent(rxStr),
        ry: this.normalizeToPercent(ryStr),
        cx: this.normalizeToPercent(cxStr),
        cy: this.normalizeToPercent(cyStr),
        rotation: 0
      };
    }
  
    return null;
  }

  getClipPath(): string {
    switch (this.shape.kind) {
      case 'polygon':
        return this.polygonClip(this.shape as PolygonShape);
      case 'inset':
        return this.insetClip(this.shape as InsetShape);
      case 'ellipse':
        return this.ellipseClip(this.shape as EllipseShape);
      case 'circle':
        return this.circleClip(this.shape as CircleShape);
      default:
        return '';
    }
  }

  private polygonClip(s: PolygonShape) {
    // if (s.units === 'percent') {
      return `polygon(${s.points.map(p => `${p.x.toFixed(2)}% ${p.y.toFixed(2)}%`).join(', ')})`;
    // } else {
    //   const rect = this.container.nativeElement.getBoundingClientRect();
    //   return `polygon(${s.points.map(p => `${(p.x / 100 * rect.width).toFixed(0)}px ${(p.y / 100 * rect.height).toFixed(0)}px`).join(', ')})`;
    // }
  }

  private insetClip(s: InsetShape) {
    // const unit = s.units === 'percent' ? '%' : 'px';
    const unit = '%';
    const parts = `${s.top}${unit} ${s.right}${unit} ${s.bottom}${unit} ${s.left}${unit}`;
    return s.borderRadius ? `inset(${parts} round ${s.borderRadius})` : `inset(${parts})`;
  }

  private ellipseClip(s: EllipseShape) {
    // const unit = s.units === 'percent' ? '%' : 'px';
    const unit = '%';
    const cx = `${s.cx}${unit}`;
    const cy = `${s.cy}${unit}`;
    const rx = `${s.rx}${unit}`;
    const ry = `${s.ry}${unit}`;
    const rot = s.rotation ? `${s.rotation}deg` : '0deg';
    // CSS syntax: ellipse(rx ry at cx cy) optionally with rotation not directly in clip-path spec,
    // we can output as 'ellipse(rx ry at cx cy)' - rotation requires transform on element.
    // For simplicity expose rotation in comment (and use svg overlay to show rotation)
    return `ellipse(${rx} ${ry} at ${cx} ${cy}) /* rotation ${rot} (visual only) */`;
  }

  private circleClip(s: CircleShape) {
    // const unit = s.units === 'percent' ? '%' : 'px';
    const unit = '%';
    const cx = `${s.cx}${unit}`;
    const cy = `${s.cy}${unit}`;
    const r = `${s.r}${unit}`
    return `circle(${r} at ${cx} ${cy})`;
  }

  // helper: client -> percent coordinates relative to container
  private clientToPercent(clientX: number, clientY: number) {
    const rect = this.container.nativeElement.getBoundingClientRect();
    let x = ((clientX - rect.left) / rect.width) * 100;
    let y = ((clientY - rect.top) / rect.height) * 100;
    x = Math.max(0, Math.min(100, x));
    y = Math.max(0, Math.min(100, y));
    return { x, y };
  }

  // snapping, shiftKey disables snap
  private snap(value: number, stepPercent: number, shiftKey = false) {
    if (shiftKey) return value;
    const step = stepPercent;
    return Math.round(value / step) * step;
  }

  // start dragging a handle
  pointerDownOnHandle(event: PointerEvent, handle: ActiveHandle) {
    try {
      (event.target as Element).setPointerCapture?.(event.pointerId);
    } catch (e) {
      // ignore if not supported
    }
    event.preventDefault();
    event.stopPropagation();
    this.active = handle;
    this.dragging = true;
  }

  // global pointermove
  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.dragging || !this.active) return;
    if (!(event instanceof PointerEvent)) return;
    const pe = event as PointerEvent;
    const rect = this.container.nativeElement.getBoundingClientRect();

    const { x: px, y: py } = this.clientToPercent(pe.clientX, pe.clientY);
    // apply snapping in percent coordinates
    const nx = this.snap(px, this.gridStepPercent, pe.shiftKey);
    const ny = this.snap(py, this.gridStepPercent, pe.shiftKey);

    switch (this.active.type) {
      case 'polygon-point': {
        if (this.shape.kind !== 'polygon') return;
        this.shape.points[this.active.index] = { x: nx, y: ny };
        break;
      }
      case 'move-shape': {
        // Move whole shape by delta (for simplicity only for polygon)
        // Not fully implemented here — placeholder
        break;
      }
      case 'inset-edge': {
        if (this.shape.kind !== 'inset') return;
        const s = this.shape as InsetShape;
        // convert percent point to edge values: top = ny, left = nx, etc.
        if (s.units !== 'percent') {
          // if px units: convert percent->px based on rect
          const topPx = ny / 100 * rect.height;
          const leftPx = nx / 100 * rect.width;
          // store as px but our model expects numbers with units flag; for simplicity we keep percent model.
        } else {
          if (this.active.edge === 'top') s.top = ny;
          if (this.active.edge === 'bottom') s.bottom = 100 - ny;
          if (this.active.edge === 'left') s.left = nx;
          if (this.active.edge === 'right') s.right = 100 - nx;
        }
        break;
      }
      case 'inset-corner': {
        if (this.shape.kind !== 'inset') return;
        const s = this.shape as InsetShape;
        if (s.units === 'percent') {
          if (this.active.corner === 'tl') {
            s.top = ny; s.left = nx;
          } else if (this.active.corner === 'tr') {
            s.top = ny; s.right = 100 - nx;
          } else if (this.active.corner === 'br') {
            s.bottom = 100 - ny; s.right = 100 - nx;
          } else if (this.active.corner === 'bl') {
            s.bottom = 100 - ny; s.left = nx;
          }
        }
        break;
      }
      case 'ellipse-center': {
        if (this.shape.kind !== 'ellipse') return;
        const s = this.shape as EllipseShape;
        s.cx = nx; s.cy = ny;
        break;
      }
      case 'ellipse-radius-x': {
        if (this.shape.kind !== 'ellipse') return;
        const s = this.shape as EllipseShape;
        // rx: distance from center to current pointer x
        const rect = this.container.nativeElement.getBoundingClientRect();
        const centerPxX = s.cx / 100 * rect.width;
        const distPx = Math.abs(pe.clientX - (rect.left + centerPxX));
        s.rx = (distPx / rect.width) * 100;
        break;
      }
      case 'ellipse-radius-y': {
        if (this.shape.kind !== 'ellipse') return;
        const s = this.shape as EllipseShape;
        const rect = this.container.nativeElement.getBoundingClientRect();
        const centerPxY = s.cy / 100 * rect.height;
        const distPx = Math.abs(pe.clientY - (rect.top + centerPxY));
        s.ry = (distPx / rect.height) * 100;
        break;
      }
      case 'ellipse-rotation': {
        if (this.shape.kind !== 'ellipse') return;
        const s = this.shape as EllipseShape;
        const rect = this.container.nativeElement.getBoundingClientRect();
        const cx = rect.left + (s.cx / 100 * rect.width);
        const cy = rect.top + (s.cy / 100 * rect.height);
        const angle = Math.atan2(pe.clientY - cy, pe.clientX - cx) * (180 / Math.PI);
        s.rotation = angle;
        break;
      }
      case 'circle-center': {
        if (this.shape.kind !== 'circle') return;
        const s = this.shape as CircleShape;
        s.cx = nx; s.cy = ny;
        break;
      }
      case 'circle-radius': {
        if (this.shape.kind !== 'circle') return;
        const s = this.shape as CircleShape;
        const rect = this.container.nativeElement.getBoundingClientRect();
        const centerPxX = s.cx / 100 * rect.width;
        const centerPxY = s.cy / 100 * rect.height;
        const distPx = Math.hypot(pe.clientX - (rect.left + centerPxX), pe.clientY - (rect.top + centerPxY));
        s.r = (distPx / Math.max(rect.width, rect.height)) * 100; // normalize to percentage of max dimension
        break;
      }
      default:
        break;
    }

    // notify angular if needed (most changes in model will be reflected)
    this.cd.markForCheck();
  }

  // pointerup release
  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent) {
    if (event instanceof PointerEvent) {
      try { (event.target as Element).releasePointerCapture?.(event.pointerId); } catch {}
    }
    this.active = null;
    this.dragging = false;
  }

  // click on SVG path to add point on nearest segment if close enough
  svgClick(event: MouseEvent) {
    if (!event.ctrlKey) return;
    if (this.shape.kind !== 'polygon') return;
    // if clicked on handle (circle) the pointerDown handles it; this is for adding on edges
    const svg = this.svgOverlay.nativeElement;
    // coordinates in percent
    const { x: px, y: py } = this.clientToPercent(event.clientX, event.clientY);
    // find nearest segment and distance (in percent-based coordinates -> approximate)
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < this.shape.points.length; i++) {
      const a = this.shape.points[i];
      const b = this.shape.points[(i + 1) % this.shape.points.length];
      const d = this.pointToSegmentDistance({ x: px, y: py }, a, b);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    // threshold: allow insertion when close enough (in percent). adjust as needed.
    const threshold = 4; // percent
    if (bestDist <= threshold) {
      const a = this.shape.points[bestIdx];
      const b = this.shape.points[(bestIdx + 1) % this.shape.points.length];
      const nx = (a.x + b.x) / 2;
      const ny = (a.y + b.y) / 2;
      this.shape.points.splice(bestIdx + 1, 0, { x: nx, y: ny });
    } else {
      // clicked not close to edge -> maybe add free point at end
      // optional: add new point at end
      // this.shape.points.push({ x: px, y: py });
    }
  }

  addPolygonPointAt(index: number) {
    if (this.shape.kind !== 'polygon') return;
    const a = this.shape.points[index];
    const b = this.shape.points[(index + 1) % this.shape.points.length];
    this.shape.points.splice(index + 1, 0, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  }

  // distance from point p to segment ab (all in percent coords)
  private pointToSegmentDistance(p: Point, a: Point, b: Point) {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const wx = p.x - a.x;
    const wy = p.y - a.y;
    const c1 = vx * wx + vy * wy;
    if (c1 <= 0) return Math.hypot(p.x - a.x, p.y - a.y);
    const c2 = vx * vx + vy * vy;
    if (c2 <= c1) return Math.hypot(p.x - b.x, p.y - b.y);
    const t = c1 / c2;
    const projx = a.x + t * vx;
    const projy = a.y + t * vy;
    return Math.hypot(p.x - projx, p.y - projy);
  }

  // remove a handle
  removePolygonPoint(i: number) {
    if (this.shape.kind !== 'polygon') return;
    if (this.shape.points.length <= 3) return;
    this.shape.points.splice(i, 1);
  }

  /** Hàm copy css vào bộ nhớ tạm */
  async copy(type: 'css' | 'value') {
    const text = type == 'css' ? `clip-path: ${this.getClipPath()};` : this.getClipPath();
    try {
      await navigator.clipboard.writeText(text);
      // optionally show feedback (toast)
      console.log('Copied:', text);
    } catch (e) {
      console.error('Copy failed', e);
    }
  }

  // shareable link encode/decode
  // makeShareableLink() {
  //   const payload = { shape: this.shape, bg: this.bgColor, img: this.backgroundImage };
  //   const encoded = btoa(JSON.stringify(payload));
  //   return `${location.origin}${location.pathname}#clip=${encoded}`;
  // }

  // load from url hash when component created
  private loadShape() {
    if (this.data.clipPath) this.shape = this.parseClipPath(this.data.clipPath);
    if (this.isColor(this.data.bgColor)) this.bgColor = this.data.bgColor;
    if (this.data.bgImage && this.data.optionDisplay == 'image') 
      this.bgImage = this.data.bgImage;
  }

  // small helper to get polygon points attr for SVG (percent -> px)
  polygonPointsAttr() {
    if (this.shape.kind !== 'polygon') return '';
    const rect = this.container.nativeElement.getBoundingClientRect();
    return this.shape.points.map(p => `${(p.x / 100 * rect.width).toFixed(1)},${(p.y / 100 * rect.height).toFixed(1)}`).join(' ');
  }

  /** Đóng popup */
  close(isSave = true) {
    if (isSave)
      this.dialogRef.close(this.getClipPath());
    else
      this.dialogRef.close();
  }

  // Lắng nghe sự kiện keydown ở cấp độ window
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case "Escape": 
        if (!this.editing) {
          event.preventDefault(); 
          this.close(false);
        }
        break;
      default:
        return;
    }
  }

  chooseShape(event, item) {
    this.shape = this.parseClipPath(item.clipPath);
  }

  private isColor(value: string): boolean {
    const s = new Option().style;
    s.color = value;
    return s.color !== "";
  }

  private normalizeToPercent(value: string): number {
    value = value.trim();
  
    // Nếu là calc(...) => cố gắng bóc tham số cuối cùng dạng % hoặc px
    if (value.startsWith('calc')) {
      // Lấy tất cả số cuối cùng
      const mPct = value.match(/([0-9.]+)%/);
      if (mPct) return parseFloat(mPct[1]);
    }
  
    // Nếu là dạng 50%
    if (value.endsWith('%')) {
      return parseFloat(value);
    }
  
    // Nếu là px → convert tạm sang % (giả sử 100px = 100%)
    // => tùy bạn điều chỉnh nếu có width/height thật
    if (value.endsWith('px')) {
      return (parseFloat(value) * 100) / 100; // mặc định 100px = 100%
    }
  
    // Nếu chỉ là số ⇒ coi như %
    return parseFloat(value);
  }

  /* ========== CHỈNH SỬA TRỰC TIẾP ========== */
  /**
   * Xử lý phím trong edit
   * (Chỉ hoạt động khi edit)
   * @param event Phím nhấn bàn phím
   */
  onEditKeydown(event: KeyboardEvent) {
    if (!this.editing) return;
    switch (event.key) {
      // Tránh ảnh hưởng của di chuyển vật thể khi bấm
      case "ArrowDown":
      case "ArrowUp":
      case "ArrowLeft":
      case "ArrowRight":
        event.stopPropagation();
        const container = event.target as HTMLElement;
        const scrollTop = container.scrollTop;
        setTimeout(() => container.scrollTop = scrollTop);
        break;
      case "Delete": // Tránh ảnh hưởng của xoá vật thể khi bấm
        event.stopPropagation();
        break;
      case "Enter":
        // Nếu textarea (multiline) thì lưu khi Ctrl+Enter; nếu input (single-line) thì Enter lưu
        if (event.ctrlKey || event.metaKey) {
          // Ctrl + Enter = Lưu
          event.preventDefault();
          this.saveEditing(event);
        }
        break;
      case "Escape": // Hủy edit và trả về giá trị trước khi chỉnh sửa
        event.preventDefault();
        this.cancelEditing();
        break;
      default:
        return;
    }
  }

  /**
   * Bắt đầu edit khi dblclick
   */
  startEditing(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.editingValue = this.getClipPath();
    this.editing = true;

    // Cho Angular render xong contentEditable = true rồi mới set caret
    setTimeout(() => {
      this.placeCaretAtEnd(this.editInput.nativeElement);
    });
  }

  /**
   * Lưu thay đổi: 
   * - Cập nhật staticdata (Nội dung chuỗi)
   * - Cập nhật class (style cho chuỗi)
   * @param event mục đích để lấy `target as HTMLElement`
   */
  saveEditing(event?: any) {
    if (!this.editing) return;
    if (event) {
      const element = event.target as HTMLElement;
      const newValue = element.innerText;

      if (newValue && newValue !== this.editingValue) {
        this.editingValue = newValue;
        this.shape = this.parseClipPath(newValue);
      }
    }
    else {
      // Cập nhật model local (nếu component nhận bằng reference, parent sẽ thấy thay đổi)
      this.shape = this.parseClipPath(this.editingValue);
    }

    this.editing = false;
    this.editingValue = "";
  }

  /**
   * Thoát chỉnh sửa trực tiếp
   */
  private cancelEditing() {
    this.editing = false;
    this.editingValue = '';
  }

  /**
   * Đặt caret ở cuối cùng của chuỗi
   */
  private placeCaretAtEnd(element: HTMLElement) {
    if (!isPlatformBrowser(this.platformId)) return;
    // Tạo range để di chuyển caret
    const range = document.createRange();
    const selection = window.getSelection();
  
    range.selectNodeContents(element);
    range.collapse(false); // false => caret ở cuối node
  
    selection?.removeAllRanges();
    selection?.addRange(range);
  
    element.focus(); // đảm bảo focus để caret hiển thị
  }
}

interface Point {
  x: number; // percent [0..100]
  y: number; // percent [0..100]
}

type Units = 'percent' | 'px';

interface PolygonShape {
  kind: 'polygon';
  points: Point[];
  units: Units;
}

interface InsetShape {
  kind: 'inset';
  top: number;    // percent or px depending on units
  right: number;
  bottom: number;
  left: number;
  borderRadius?: string; // e.g. "10% 10% 0 0"
  units: Units;
}

interface EllipseShape {
  kind: 'ellipse';
  cx: number; // center x percent/px
  cy: number; // center y
  rx: number; // radius x
  ry: number; // radius y
  rotation: number; // degrees
  units: Units;
}

interface CircleShape {
  kind: 'circle';
  cx: number;
  cy: number;
  r: number;
  units: Units;
}

type ClipShape = PolygonShape | InsetShape | EllipseShape | CircleShape;

type ActiveHandle =
  | { type: 'polygon-point'; index: number }
  | { type: 'inset-edge'; edge: 'top' | 'right' | 'bottom' | 'left' }
  | { type: 'inset-corner'; corner: 'tl' | 'tr' | 'br' | 'bl' | string }
  | { type: 'move-shape' }
  | { type: 'ellipse-center' }
  | { type: 'ellipse-radius-x' }
  | { type: 'ellipse-radius-y' }
  | { type: 'ellipse-rotation' }
  | { type: 'circle-center' }
  | { type: 'circle-radius' }
  | null;